import { action, computed, makeObservable } from 'mobx';

import { ViewTable } from '@/configs/ViewTable';
import { ValueModel } from '@/models/ValueModel';

import { ModeDnd, TableSortOrder, type WorkTime } from './types';

export class ViewSettingsStore {
  private readonly _dndMode: ValueModel<ModeDnd> = new ValueModel<ModeDnd>(ModeDnd.Moving);
  private readonly _view: ValueModel<ViewTable> = new ValueModel<ViewTable>(ViewTable.Week);
  private readonly _hideWeekends: ValueModel<boolean> = new ValueModel<boolean>(false);
  private readonly _workTime: ValueModel<WorkTime> = new ValueModel<WorkTime>('40');
  private readonly _calendarExpandAll: ValueModel<boolean> = new ValueModel<boolean>(false);
  private readonly _tableSortOrder: ValueModel<TableSortOrder> = new ValueModel<TableSortOrder>(
    TableSortOrder.Time,
  );
  private readonly _addTimeWithQuickButtons: ValueModel<boolean> = new ValueModel<boolean>(false);

  constructor() {
    makeObservable(this, {
      dndMode: computed,
      view: computed,
      hideWeekends: computed,
      workTime: computed,
      calendarExpandAll: computed,
      tableSortOrder: computed,
      addTimeWithQuickButtons: computed,
      setWorkTime: action.bound,
      setHideWeekends: action.bound,
      setDndMode: action.bound,
      setView: action.bound,
      setCalendarExpandAll: action.bound,
      setTableSortOrder: action.bound,
      setAddTimeWithQuickButtons: action.bound,
    });

    this.loadFromStorage();
  }

  get workTime() {
    return this._workTime.value;
  }

  get hideWeekends() {
    return this._hideWeekends.value;
  }

  get dndMode() {
    return this._dndMode.value;
  }

  get view() {
    return this._view.value;
  }

  get calendarExpandAll() {
    return this._calendarExpandAll.value;
  }

  get tableSortOrder() {
    return this._tableSortOrder.value;
  }

  get addTimeWithQuickButtons() {
    return this._addTimeWithQuickButtons.value;
  }

  setWorkTime = (time: WorkTime) => {
    this._workTime.change(time);
  };

  setHideWeekends = (value: boolean) => {
    this._hideWeekends.change(value);
    this.saveToStorage();
  };

  setDndMode = (mode: ModeDnd) => {
    this._dndMode.change(mode);
    this.saveToStorage();
  };

  setView = (view: ViewTable) => {
    if (view === this.view) return;

    this._view.change(view);
    this.saveToStorage();
  };

  setCalendarExpandAll = (value: boolean) => {
    this._calendarExpandAll.change(value);
  };

  setTableSortOrder = (order: TableSortOrder) => {
    this._tableSortOrder.change(order);
    this.saveToStorage();
  };

  setAddTimeWithQuickButtons = (value: boolean) => {
    this._addTimeWithQuickButtons.change(value);
    this.saveToStorage();
  };

  private saveToStorage() {
    localStorage.setItem(
      'ui-settings',
      JSON.stringify({
        dndMode: this.dndMode,
        view: this.view,
        hideWeekends: this.hideWeekends,
        tableSortOrder: this.tableSortOrder,
        addTimeWithQuickButtons: this.addTimeWithQuickButtons,
      }),
    );
  }

  private loadFromStorage() {
    const raw = localStorage.getItem('ui-settings');

    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);

      if (Object.values(ModeDnd).includes(parsed.dndMode)) {
        this.setDndMode(parsed.dndMode);
      }

      if (Object.values(ViewTable).includes(parsed.view)) {
        this.setView(parsed.view);
      }

      if (typeof parsed.hideWeekends === 'boolean') {
        this.setHideWeekends(parsed.hideWeekends);
      }

      if (Object.values(TableSortOrder).includes(parsed.tableSortOrder)) {
        this.setTableSortOrder(parsed.tableSortOrder);
      }

      if (typeof parsed.addTimeWithQuickButtons === 'boolean') {
        this.setAddTimeWithQuickButtons(parsed.addTimeWithQuickButtons);
      }
    } catch (err) {
      console.error(err, 'Ошибка');
    }
  }
}

export const viewSettingsStore = new ViewSettingsStore();
